import AnimationController from '../../../assets/js/controllers/animation';
import ResizeController from '../../../assets/js/controllers/resize';
import ScrollController from '../../../assets/js/controllers/scroll';
import SkrolliteController from '../../../assets/js/controllers/skrollite';

import Platform from '../../../assets/js/constants/platform';

import { classesActiveAndIn, getRootOffsetBounds, multiAddEventListener, multiClassList } from '../../../assets/js/helpers';

export default class NavigationPrimary {

	static selector = 'navigation-primary';

	// class variables

	el = {};

	controllers = {
		animation: null,
		resize: null
	};

	coords = {
		hero: null,
		footer: null
	};

	flags = {
		isExpanded: false,
		isMouseOver: false,
		isMobile: false,
		isScrollDisabled: false,
		isSticky: false,
		willStick: false
	};

	states = {
		activeNavItem: null,
		activeExpandingMenu: null
	};

	timeouts = {
		unstick: null,
		collapse: null
	};

	// constructor

	constructor(container) {
		this.el = {
			container,
			ctas: container.querySelectorAll(`.${this.constructor.selector}__link a`),
			expandingCtas: container.querySelectorAll(`.${this.constructor.selector}__link.is-expanding a, button`),
			expandingMenus: container.querySelectorAll(`.${this.constructor.selector}__expanded-desktop`),
			hamburger: container.querySelector(`.${this.constructor.selector}__hamburger`),
			hero: document.body.querySelector('.hero'),
			footer: document.body.querySelector('.footer'),
			teamDMTrigger: document.getElementById('team-dm-dropdown-toggle')
		};

		if (!this.el.hero) {
			this.el.hero = document.body.querySelector('.submodule-hero');
		}

		this.initialize();
	}

	initialize() {
		// ScrollController.disableSmoothScrolling(true);
		// SkrolliteController.disable();

		this.flags.isScrollDisabled = this.el.container.classList.contains('is-scroll-disabled');

		this.#addEventListeners();
	}

	// event listeners

	#addEventListeners = () => {

		this.el.container.addEventListener('mouseenter', this.#onMouseEnter);
		this.el.container.addEventListener('mouseleave', this.#onMouseLeave);

		if (this.el.hamburger) {
			this.el.hamburger.addEventListener('click', this.#onHamburgerClick);
		}
		multiAddEventListener(this.el.expandingCtas, 'mouseenter', this.#onExpandingCtaHover);
		multiAddEventListener(this.el.ctas, 'mouseenter', this.#onCtaHover);

		multiAddEventListener(this.el.expandingCtas, 'focus', this.#onExpandingCtaHover);
		multiAddEventListener(this.el.ctas, 'focus', this.#onCtaHover);

		// multiAddEventListener(this.el.ctas, 'click', this.#onCtaClick);

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});

		this.controllers.animation = AnimationController.set({
			update: this.#onAnimationUpdate,
			render: this.#onAnimationRender
		});

		this.#measureResize();
	}

	#removeEventListeners = () => {
		this.el.container.removeEventListener('mouseenter', this.#onMouseEnter);
		this.el.container.removeEventListener('mouseleave', this.#onMouseLeave);
		if (this.el.hamburger) {
			this.el.hamburger.removeEventListener('click', this.#onHamburgerClick);
		}

		multiRemoveEventListener(this.el.expandingCtas, 'mouseenter', this.#onExpandingCtaHover);
		multiRemoveEventListener(this.el.ctas, 'mouseenter', this.#onCtaHover);

		multiRemoveEventListener(this.el.expandingCtas, 'focus', this.#onExpandingCtaHover);
		multiRemoveEventListener(this.el.ctas, 'focus', this.#onCtaHover);

		// multiRemoveEventListener(this.el.ctas, 'click', this.#onCtaClick);

		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);
	}

	// listener methods

	#onHamburgerClick = () => {
		if (this.el.container.classList.contains('is-expanded-mobile')) {
			this.collapseMobile();
		} else {
			this.expandMobile();
		}
	}

	#onCtaHover = (e) => {
		if (!this.flags.isMobile) {
			const $target = e.currentTarget;
			const $link = $target.closest(`.${this.constructor.selector}__link`);
			if (this.states.activeNavItem !== $link && this.states.activeNavItem) {
				this.collapseTimeoutStart();
			}
		}
	}

	#onExpandingCtaHover = (e) => {
		if (!this.flags.isMobile) {
			const $target = e.currentTarget;
			const $link = $target.closest(`.${this.constructor.selector}__link`);
			this.expand($link);
		}
	}

	#onMouseEnter = () => {
		this.flags.isMouseOver = true;
		if (this.states.isExpanded) {
			this.collapseTimeoutCancel();
		}
	}

	#onMouseLeave = () => {
		this.flags.isMouseOver = false;
		if (this.states.isExpanded) {
			this.collapseTimeoutStart();
		}
	}

	#onResizeUpdate = () => {
		const wasMobile = this.flags.isMobile;
		let willRender = false;

		this.flags.isMobile = (ResizeController.platform === Platform.MOBILE);
		this.#measureResize();

		if (this.flags.isMobile) {
			if (!wasMobile) {
				willRender = true;
			}
		} else if (wasMobile) {
			willRender = true;
		}

		return willRender;
	}

	#onResizeRender = () => {
		if (!this.flags.isMobile && this.el.container.classList.contains('is-expanded-mobile')) {
			this.collapseMobile();
		} else if (this.flags.isMobile && this.el.container.classList.contains('is-expanded')) {
			this.collapseAllAndClose();
		}
	}

	#onAnimationUpdate = () => {
		const willStick = this.measureStickStatus();
		this.flags.willStick = willStick;
		return (this.flags.isSticky !== willStick);
	}

	#onAnimationRender = () => {
		this.adjustStickStatus();
	}

	#onCtaClick = (e) => {
		const target = (e.target.tagName.toLowerCase() === 'a') ? e.target : e.target.closest('a');
		if (target.classList.contains('js-module-link')) {
			this.ctaAnchorClick(target);
		}
	}

	// measurement

	#measureResize = () => {
		if (this.el.hero) {
			this.coords.hero = getRootOffsetBounds(this.el.hero);
			this.coords.hero.bottom += parseInt(getComputedStyle(this.el.hero).marginBottom);
		} else {
			this.coords.hero = {};
			this.coords.hero.bottom = ScrollController.viewportHeight;
		}

		this.coords.footer = getRootOffsetBounds(this.el.footer);
	}

	// expand collapse desktop menu

	expand($target) {
		this.collapseTimeoutCancel();
		if ($target && $target !== this.states.activeNavItem) {
			this.collapseAll();
			this.states.activeNavItem = $target;
			this.states.activeExpandingMenu = this.getExpandingMenuByIndex($target.dataset.index);
			this.states.isExpanded = true;

			if (!this.el.container.classList.contains('is-expanded')) {
				classesActiveAndIn(this.el.container, 'is-expanded', 'is-expanded-in');
			}

			this.states.activeExpandingMenu.classList.add('is-active');


			// Update heights and add active class to first anchor
			const allItems = this.states.activeExpandingMenu.querySelector(`.${this.constructor.selector}__expanded-defaults-col li.navigation-primary__expanded-default-item`);
			const firstItem = this.states.activeExpandingMenu.querySelector(`.${this.constructor.selector}__expanded-defaults-col ul:first-of-type li.navigation-primary__expanded-default-item:first-child`);
			const expandedCol = this.states.activeExpandingMenu.querySelector(`.${this.constructor.selector}__expanded-defaults-col`);
			const allHovers = this.states.activeExpandingMenu.querySelectorAll(`.${this.constructor.selector}__expanded-default-item-hover`);

			// Is no min-height is set, find tallest element and set min-height
			if (!expandedCol.style.minHeight) {
				let newMinHeight = (expandedCol.clientHeight > 0) ? expandedCol.clientHeight : 100;

				// find tallest element
				for (let i = allHovers.length - 1; i >= 0; i--) {
					if (allHovers[i].clientHeight > newMinHeight) {
						newMinHeight = allHovers[i].clientHeight;
					}
				}

				// Set min-height on Column (left nav) if it is shorter than the tallest hover element
				expandedCol.style.minHeight = `${newMinHeight}px`;
			}

			// Add initial  hover class to activate the first element
			multiClassList(allItems, 'remove', ['is-hover', 'is-hover-in']);
			firstItem.classList.add('is-hover', 'is-hover-in');
		}
	}

	getExpandingMenuByIndex(index) {
		let $expandingMenu = null;

		for (let i = 0; i < this.el.expandingMenus.length; i++) {
			const $curExpandingMenu = this.el.expandingMenus[i];
			if ($curExpandingMenu.dataset.index === index) {
				$expandingMenu = $curExpandingMenu;
			}
		}

		return $expandingMenu;
	}

	collapseTarget($target) {
		if (this.states.activeExpandingMenu === $target) {
			this.collapseAllAndClose();
		} else {
			$target.classList.remove('is-active');
		}
	}

	collapseAll() {
		multiClassList(this.el.expandingMenus, 'remove', ['is-active']);
		this.states.activeExpandingMenu = null;
		this.states.activeNavItem = null;

		if (this.el.teamDMTrigger.checked) {
			this.el.teamDMTrigger.click();
		}
	}

	collapseAllAndClose() {
		this.el.container.classList.remove('is-expanded', 'is-expanded-in');
		this.states.isExpanded = false;

		this.collapseAll();
		this.collapseTimeoutCancel();
	}

	collapseTimeoutStart() {
		if (!this.timeouts.collapse) {
			this.el.container.classList.add('is-expanded-out');
			this.timeouts.collapse = setTimeout(() => {
				this.timeouts.collapse = null;
				this.collapseAllAndClose();
			}, 400);
		}
	}

	collapseTimeoutCancel() {
		this.el.container.classList.remove('is-expanded-out');
		if (this.timeouts.collapse) {
			clearTimeout(this.timeouts.collapse);
			this.timeouts.collapse = null;
		}
	}

	// expand / collapse mobile menu

	expandMobile() {
		ScrollController.lock();
		AnimationController.one({
			update: () => true,
			render: () => {
				classesActiveAndIn(this.el.container, 'is-expanded-mobile', 'is-expanded-mobile-in');
			}
		});
	}

	collapseMobile() {
		ScrollController.unlock();
		AnimationController.one({
			update: () => true,
			render: () => {
				this.el.container.classList.remove('is-expanded-mobile', 'is-expanded-mobile-in');
			}
		});
	}

	ctaAnchorClick(el) {
		let moduleId = el.getAttribute('href');
		moduleId = (moduleId.includes('#')) ? moduleId : `#${moduleId}`;
		const module = document.querySelector(moduleId);

		ScrollController.scrollTo(module.getBoundingClientRect().top, false);

		// Refresh Skrollite/etc.
		ScrollController.refresh();
		ScrollController.updateChildren();
		SkrolliteController.pageUpdate();
	}

	// make sticky / unstick (desktop only)

	stick() {
		if (!this.flags.isSticky) {
			this.flags.willStick = true;
			this.flags.isSticky = true;
			if (!this.el.container.getAttribute('class').includes('not-sticky')) {
				this.el.container.classList.remove('is-sticky-out');

				if (this.timeouts.unstick) {
					clearTimeout(this.timeouts.unstick);
					this.timeouts.unstick = null;
				}


				ScrollController.stick(this.el.container);
				classesActiveAndIn(this.el.container, 'is-sticky', 'is-sticky-in');
			}
		}
	}

	unstick() {
		if (this.flags.isSticky) {
			this.flags.willStick = false;
			this.flags.isSticky = false;

			if (!this.el.container.getAttribute('class').includes('not-sticky')) {
				this.el.container.classList.remove('is-sticky-in');
				this.el.container.classList.add('is-sticky-out');

				this.collapseAllAndClose();

				this.timeouts.unstick = setTimeout(() => {
					this.timeouts.unstick = null;
					this.el.container.classList.remove('is-sticky', 'is-sticky-out');
					ScrollController.unstick(this.el.container);
					if (this.measureStickStatus()) {
						this.adjustStickStatus();
					}
				}, 600);
			}
		}
	}

	//

	measureStickStatus() {
		const scrollTop = ScrollController.top;
		const scrollBottom = scrollTop + window.innerHeight;
		let willStick = !this.flags.isScrollDisabled;

		if (scrollTop < this.coords.hero.bottom || scrollBottom > this.coords.footer.top) {
			// only displays when below hero or above footer
			willStick = false;
		}

		if (ScrollController.direction > 0) {
			willStick = false;
		}

		return willStick;
	}

	adjustStickStatus() {
		if (this.flags.willStick) {
			this.stick();
		} else {
			this.unstick();
		}
	}

	// destroy

	destroy() {
		this.#removeEventListeners();
	}

}
